import z from 'zod';

/**
 * Environment configuration for @az3fr/web
 *
 * Vite exposes environment variables through import.meta.env
 * Only variables prefixed with VITE_ are available in the browser.
 *
 * To make variables available to the web app:
 * 1. Add them to root .env with VITE_ prefix
 * 2. Reference them here
 * 3. Vite will automatically inject them at build time
 *
 * Note: This file is bundled into the client, so never include secrets here.
 */

const webEnvSchema = z.object({
	// Vite's built-in variables
	MODE: z.enum(['development', 'production', 'test']).default('development'),
	DEV: z.boolean().default(true),
	PROD: z.boolean().default(false),

	// API Configuration
	VITE_API_URL: z.url().default('http://localhost:3000'),
	VITE_API_TIMEOUT: z.coerce.number().int().positive().default(30_000),
});

type WebEnv = z.infer<typeof webEnvSchema>;

function validateEnv(): WebEnv {
	try {
		const validated = webEnvSchema.parse({
			MODE: import.meta.env.MODE,
			DEV: import.meta.env.DEV,
			PROD: import.meta.env.PROD,
			VITE_API_URL: import.meta.env.VITE_API_URL,
			VITE_API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT,
		});

		return validated;
	} catch (error) {
		console.error('❌ Invalid environment configuration for @az3fr/web:', error);
		// Don't exit in browser, just log the error
		throw error;
	}
}

const env = validateEnv();

// Log loaded environment (only in development)
if (env.DEV) {
	console.log('✅ @az3fr/web environment loaded:', {
		MODE: env.MODE,
		VITE_API_URL: env.VITE_API_URL,
		VITE_API_TIMEOUT: env.VITE_API_TIMEOUT,
	});
}

export { env };
export type { WebEnv };
